<template>
  <v-dialog v-model="dialog" persistent>
    <v-form ref="form">
      <v-card>
        <v-card-title>
          <v-chip
            label
            outlined
            :color="childInfo.color"
            class="font-weight-bold mr-2"
          >{{ childInfo.id }}</v-chip>
          <v-avatar :color="childInfo.color" size="36" class="mr-2">
            <span class="white--text">
              <strong>{{ childInfo.chipInitials }}</strong>
            </span>
          </v-avatar>
          <strong class="mr-2">{{ childInfo.childFirstName }}</strong>
          &nbsp;
          {{ editedItem.subject }} Grade {{ editedItem.gradeLevel }}
          <v-spacer></v-spacer>
          <v-chip class="mr-2">
            <v-checkbox v-model="editedItem.isFinal" label="Finalize Marks" class="mr-2"></v-checkbox>
          </v-chip>
          <v-btn text v-on:click="$emit('close')">Cancel</v-btn>
          <v-btn color="primary" v-on:click="saveForm">Save</v-btn>
        </v-card-title>
        
        <v-card-title>
          <v-chip class="mr-2">
            {{ computedExamGradeTypeDescription }} 
          </v-chip>
          <v-chip class="mr-2">
            Theory ({{editedItem.totalTheoryMark}})  
            Practical ({{editedItem.totalPracticalMark}})  
          </v-chip>
          <v-chip class="mr-2">
            Calculated Theory ({{editedItem.calcTheoretical}}) 
            + 
            Calculated Practical ({{editedItem.calcPractical}})
            =  
            Calculated Total ({{editedItem.totalMark}})
          </v-chip>
        </v-card-title>
        <v-card-text>
          <v-container class="inputs-container">
            <!-- theoretical exam -->
            <v-row v-if="editedItem.gradeLevel !== 1" dense>
              <v-col cols="6" sm="2">
                <v-datetime-picker
                  label="Theoretical Exam Date"
                  v-model="editedItem.theoreticalExamDate"
                  :disabled="editedItem.isFinal"
                >
                  <template slot="dateIcon">
                    <v-icon>mdi-calendar</v-icon>
                  </template>
                  <template slot="timeIcon">
                    <v-icon>mdi-clock-outline</v-icon>
                  </template>
                  <template slot="actions" slot-scope="{ parent }">
                    <v-btn color="primary" outlined @click.native="parent.clearHandler">Clear</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text @click.native="parent.resetPicker">Cancel</v-btn>
                    <v-btn color="primary" @click="parent.okHandler">Done</v-btn>
                  </template>
                </v-datetime-picker>
              </v-col>

              <v-col cols="6" sm="3">
                <v-select
                  :items="locationsActive"
                  label="Theoretical Exam Location"
                  clearable
                  :disabled="editedItem.isFinal"
                  v-model="editedItem.theoryLocation"
                ></v-select>
              </v-col>

              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.theoryEvaluatorId"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Theoretical Evaluator"
                  item-value="id"
                  clearable
                  :filter="teacherFilter"
                  :disabled="editedItem.isFinal"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.theoryMark"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  :disabled="editedItem.isFinal"
                  clearable
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- theoretical grace -->
            <v-row v-if="editedItem.gradeLevel !== 1" dense>
              <v-spacer></v-spacer>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="editedItem.theoreticalGraceMark"
                  type="number"
                  label="Theoretical Grace Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>
            <!-- practical exams -->
            <v-row v-if="!editedItem.isRetry" dense>
              <v-col cols="6" sm="2">
                <v-datetime-picker
                  label="Practical Exam Date"
                  v-model="editedItem.practicalExamDate"
                  :disabled="editedItem.isFinal"
                >
                  <template slot="dateIcon">
                    <v-icon>mdi-calendar</v-icon>
                  </template>
                  <template slot="timeIcon">
                    <v-icon>mdi-clock-outline</v-icon>
                  </template>
                  <template slot="actions" slot-scope="{ parent }">
                    <v-btn color="primary" outlined @click.native="parent.clearHandler">Clear</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text @click.native="parent.resetPicker">Cancel</v-btn>
                    <v-btn color="primary" @click="parent.okHandler">Done</v-btn>
                  </template>
                </v-datetime-picker>
              </v-col>

              <v-col cols="6" sm="3">
                <v-select
                  :items="locationsActive"
                  label="Practical Exam Location"
                  clearable
                  :disabled="editedItem.isFinal"
                  v-model="editedItem.practicalLocation"
                ></v-select>
              </v-col>

              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.practicalEvaluatorId1"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Practical Evaluator 1"
                  item-value="id"
                  clearable
                  :disabled="editedItem.isFinal"
                  :filter="teacherFilter"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.practicalMark1"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row
              dense
              v-if="!editedItem.isRetry && (editedItem.practicalEvaluatorId1 || editedItem.practicalMark1 || editedItem.practicalEvaluatorId2 || editedItem.practicalMark2)"
            >
              <v-spacer></v-spacer>
              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.practicalEvaluatorId2"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Practical Evaluator 2"
                  item-value="id"
                  clearable
                  :disabled="editedItem.isFinal"
                  :filter="teacherFilter"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.practicalMark2"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row
              dense
              v-if="!editedItem.isRetry && (editedItem.practicalEvaluatorId2 || editedItem.practicalMark2 || editedItem.practicalEvaluatorId3 || editedItem.practicalMark3)"
            >
              <v-spacer></v-spacer>
              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.practicalEvaluatorId3"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Practical Evaluator 3"
                  item-value="id"
                  clearable
                  :disabled="editedItem.isFinal"
                  :filter="teacherFilter"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.practicalMark3"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row
              dense
              v-if="!editedItem.isRetry && (editedItem.practicalEvaluatorId3 || editedItem.practicalMark3 || editedItem.practicalEvaluatorId4 || editedItem.practicalMark4)"
            >
              <v-spacer></v-spacer>
              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.practicalEvaluatorId4"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Practical Evaluator 4"
                  item-value="id"
                  clearable
                  :disabled="editedItem.isFinal"
                  :filter="teacherFilter"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.practicalMark4"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row
              dense
              v-if="!editedItem.isRetry && (editedItem.practicalEvaluatorId4 || editedItem.practicalMark4 || editedItem.practicalEvaluatorId5 || editedItem.practicalMark5)"
            >
              <v-spacer></v-spacer>
              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.practicalEvaluatorId5"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Practical Evaluator 5"
                  item-value="id"
                  clearable
                  :disabled="editedItem.isFinal"
                  :filter="teacherFilter"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.practicalMark5"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- oral exams -->
            <v-row v-if="!editedItem.isRetry" dense>
              <v-col cols="6" sm="2">
                <v-datetime-picker
                  label="Oral Exam Date"
                  v-model="editedItem.oralExamDate"
                  :disabled="editedItem.isFinal"
                >
                  <template slot="dateIcon">
                    <v-icon>mdi-calendar</v-icon>
                  </template>
                  <template slot="timeIcon">
                    <v-icon>mdi-clock-outline</v-icon>
                  </template>
                  <template slot="actions" slot-scope="{ parent }">
                    <v-btn color="primary" outlined @click.native="parent.clearHandler">Clear</v-btn>
                    <v-spacer></v-spacer>
                    <v-btn text @click.native="parent.resetPicker">Cancel</v-btn>
                    <v-btn color="primary" @click="parent.okHandler">Done</v-btn>
                  </template>
                </v-datetime-picker>
              </v-col>

              <v-col cols="6" sm="3">
                <v-select
                  :items="locationsActive"
                  label="Oral Exam Location"
                  clearable
                  :disabled="editedItem.isFinal"
                  v-model="editedItem.oralLocation"
                ></v-select>
              </v-col>

              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.oralEvaluatorId1"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Oral Evaluator 1"
                  item-value="id"
                  clearable
                  :disabled="editedItem.isFinal"
                  :filter="teacherFilter"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.oralMark1"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row
              dense
              v-if="!editedItem.isRetry && (editedItem.oralEvaluatorId1 || editedItem.oralMark1 || editedItem.oralEvaluatorId2 || editedItem.oralMark2)"
            >
              <v-spacer></v-spacer>
              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.oralEvaluatorId2"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Oral Evaluator 2"
                  item-value="id"
                  clearable
                  :disabled="editedItem.isFinal"
                  :filter="teacherFilter"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.oralMark2"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <v-row
              dense
              v-if="!editedItem.isRetry && (editedItem.oralEvaluatorId2 || editedItem.oralMark2 || editedItem.oralEvaluatorId3 || editedItem.oralMark3)"
            >
              <v-spacer></v-spacer>
              <v-col cols="8" sm="5">
                <v-autocomplete
                  v-model="editedItem.oralEvaluatorId3"
                  :items="teachers.filter((t) => t.isEvaluator)"
                  label="Oral Evaluator 3"
                  item-value="id"
                  clearable
                  :disabled="editedItem.isFinal"
                  :filter="teacherFilter"
                >
                  <template v-slot:item="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                  <template v-slot:selection="teacher">
                    {{ teacher.item.firstName }}
                    {{ teacher.item.lastName }}
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="4" sm="2">
                <v-text-field
                  v-model="editedItem.oralMark3"
                  type="number"
                  label="Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- oral grace -->
            <v-row v-if="!editedItem.isRetry" dense>
              <v-spacer></v-spacer>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="editedItem.oralGraceMark"
                  type="number"
                  label="Oral Grace Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>

            <!-- grace marks -->
            <v-row dense>
              <v-col cols="6" sm="2">Marks Last Modified by: {{ editedItem.markEntryUserId }}</v-col>
              <v-col cols="6" sm="3">Date: {{ editedItem.marksModifiedDate | formatDateTime}}</v-col>
              <v-spacer></v-spacer>
              <v-col cols="12" sm="3">
                <v-text-field
                  v-model="editedItem.graceMark"
                  type="number"
                  label="Grace Mark"
                  :rules="markRules"
                  clearable
                  :disabled="editedItem.isFinal"
                ></v-text-field>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>
      </v-card>
    </v-form>
  </v-dialog>
</template>
<script>
import { mapActions, mapState } from "vuex";
import { errorMessageMixin, examGradeTypeMixin } from "../shared";
export default {
  props: ["editedItem", "childInfo"],
  mixins: [errorMessageMixin, examGradeTypeMixin],
  computed: {
    ...mapState(["examGradeType", "teachers", "locations"]),
    locationsActive: function() {
      return this.locations.filter(x => x.isActive).map(a => a.description);
    },
    formTitle() {
      return this.editedIndex === -1 ? "Add Location" : "Edit Location";
    }
  },
  async created() {
    console.log("childInfo", this.childInfo);
    if (!this.locations || this.locations.length === 0) {
      console.log("loading locations");
      await this.loadLocations();
    }
    console.log("done created");
  },
  data() {
    return {
      dialog: true,
      // locations: process.env.VUE_APP_LOCATIONS.split(","),
      markRules: [v => !v || (v > 0 && v <= 100) || "Must be between 1 and 100"]
    };
  },
  methods: {
    ...mapActions(["getLocationsAction"]),
    async loadLocations() {
      try {
        await this.getLocationsAction();
      } catch (err) {
        this.$store.state.error = this.errorMessage(err);
        this.$store.state.snackbar = true;
        console.log(err);
      }
    },

    saveForm() {
      console.log("update with in component", this.editedItem);
      if (!this.$refs.form.validate()) {
        return;
      }
      this.$emit("save");
    },
    teacherFilter(item, queryText) {
      console.log("filter item", item);
      const firstName = (item.firstName ?? "").toLowerCase();
      const lastName = (item.lastName ?? "").toLowerCase();
      const searchText = queryText.toLowerCase();

      return (
        firstName.indexOf(searchText) > -1 || lastName.indexOf(searchText) > -1
      );
    }
  }
};
</script>
